import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createSherexOption,
  deleteSherexOption,
  getSherexOption,
  getSherexPricePreview,
  listSherexOptionGroups,
  listSherexOptions,
  listSherexPriceProducts,
  updateSherexOption,
  type CreateSherexOptionBody,
  type ListSherexOptionsParams,
  type PaginatedSherexOptions,
  type SherexOption,
  type SherexOptionGroups,
  type SherexOptionRegion,
  type SherexPricePreview,
  type SherexPriceProducts,
  type UpdateSherexOptionBody,
} from "../api/sherexOptions";

const ROOT_KEY = "sherex-options";

export function useSherexOptions(params: ListSherexOptionsParams) {
  return useQuery<PaginatedSherexOptions>({
    queryKey: [ROOT_KEY, "list", params],
    queryFn: () => listSherexOptions(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * `region` is part of the query key, not just the request: the EU and NA pages
 * are two mounts of one component, and an id scoped to EU is a different
 * resource from the same id scoped to NA (the latter 404s). Sharing one cache
 * entry would let one mount serve the other's cached row.
 */
export function useSherexOption(
  optionId: string | null,
  region?: SherexOptionRegion,
) {
  return useQuery<SherexOption>({
    queryKey: [ROOT_KEY, "detail", region ?? null, optionId],
    queryFn: () => getSherexOption(optionId as string, region),
    enabled: !!optionId,
    staleTime: 30_000,
  });
}

export function useSherexOptionGroups() {
  return useQuery<SherexOptionGroups>({
    queryKey: [ROOT_KEY, "groups"],
    queryFn: () => listSherexOptionGroups(),
    staleTime: 5 * 60_000,
  });
}

/**
 * Add-modal price preview — resolves the shared pricing group for a
 * not-yet-saved (optionId, productGroup) pair so the admin sees the existing
 * price + how many options already share it BEFORE saving. Caller enables
 * this only once both fields are filled in (see the page's debounce).
 */
export function useSherexPricePreview(
  optionId: string | null,
  productGroup: string | null,
) {
  return useQuery<SherexPricePreview>({
    queryKey: [ROOT_KEY, "price-preview", optionId, productGroup],
    queryFn: () => getSherexPricePreview(optionId as string, productGroup as string),
    enabled: !!optionId && !!productGroup,
    staleTime: 10_000,
  });
}

/**
 * Pricing products of the selected group, for the Add-modal Product dropdown.
 * Enabled only once a group is picked — the group is the query's whole input.
 */
export function useSherexPriceProducts(productGroup: string | null) {
  return useQuery<SherexPriceProducts>({
    queryKey: [ROOT_KEY, "price-products", productGroup],
    queryFn: () => listSherexPriceProducts(productGroup as string),
    enabled: !!productGroup,
    staleTime: 30_000,
  });
}

export function useCreateSherexOption() {
  const queryClient = useQueryClient();
  return useMutation<SherexOption, Error, CreateSherexOptionBody>({
    mutationFn: (body) => createSherexOption(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}

/** `region` scopes the write to the calling page's catalog (see the api module). */
export function useUpdateSherexOption() {
  const queryClient = useQueryClient();
  return useMutation<
    SherexOption,
    Error,
    { optionId: string; body: UpdateSherexOptionBody; region?: SherexOptionRegion }
  >({
    mutationFn: ({ optionId, body, region }) =>
      updateSherexOption(optionId, body, region),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}

/**
 * Soft-delete via DELETE /v1/sherex-options/:optionId, scoped to the calling
 * page's catalog so an id from the other page 404s instead of deactivating.
 */
export function useDeleteSherexOption() {
  const queryClient = useQueryClient();
  return useMutation<
    { success: true },
    Error,
    { optionId: string; region?: SherexOptionRegion }
  >({
    mutationFn: ({ optionId, region }) => deleteSherexOption(optionId, region),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ROOT_KEY] });
    },
  });
}
